import classNames from 'classnames';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RouterProvider } from 'react-router';

import { useCopilotMessageSubscriber } from '../chatbot/hooks';
import { Loader } from '../common/components/Loader';
import { useFireCaraReturnBi } from '../common/hooks/useFireCaraReturnBi';
import { useIsCopilotRedesign } from '../common/hooks/useIsCopilotRedesign';
import { useStartChatListener } from '../common/hooks/useStartChatListener';
import { selectStartChatError, selectStartChatLoading } from '../common/selectors/chat';
import { ErrorScreen } from '../error';
import { renderModel } from '../util/renderModel';
import { appDataHooks } from './App.dataHooks';
import s from './App.scss';
import { useInitialQueryFromUrl } from './hooks/useInitialQueryFromUrl';
import { useInitialReferralFromUrl } from './hooks/useInitialReferralFromUrl';
import { useLoadApp } from './hooks/useLoadApp';
import { useMarkAsReadOnTabChange } from './hooks/useMarkAsReadOnTabChange';
import { useMessageSubscriber } from './hooks/useMessageSubscriber';
import { router } from './routes/router';

const isCopilot = renderModel.isCopilot();

export const App: FC = () => {
  // listen to application state changes and start over chat if needed
  useStartChatListener();

  // fire BI 4:102 once when returning from Cara (URL has referral=cara&kera_session_id=...)
  useFireCaraReturnBi();

  // subscribe to duplexer events
  useMessageSubscriber();

  // listen to tab changes and mark messages as read
  useMarkAsReadOnTabChange();

  useInitialReferralFromUrl();
  useInitialQueryFromUrl();
  useLoadApp();

  useCopilotMessageSubscriber();

  const isCopilotRedesign = useIsCopilotRedesign();
  const className = classNames(s.root, {
    [s.rootHideWixHeader]: renderModel.hideWixHeader() || !isCopilot,
    [s.copilot]: isCopilot,
    [s.copilotRedesign]: isCopilotRedesign,
  });

  const startChatLoading = useSelector(selectStartChatLoading);
  const hasStartChatError = useSelector(selectStartChatError);

  const rootComponent = useMemo(() => {
    if (startChatLoading !== false) {
      return <Loader dataHook={appDataHooks.loader()} height="100%" verticalAlign="middle" width="100%" />;
    }

    return hasStartChatError ? <ErrorScreen /> : <RouterProvider router={router} />;
  }, [startChatLoading, hasStartChatError]);

  return (
    <div className={className} data-hook={appDataHooks.appContainer({})}>
      {rootComponent}
    </div>
  );
};

export default App;
